import { useRef, useEffect, RefObject } from 'react';
import { getAnimationEngine } from '../utils/animationEngine';
import type { Direction, MovementStyle, Position } from '../types';

/**
 * useBLSAnimation - Hook for managing visual animations
 *
 * Features:
 * - Smooth 60fps animations
 * - Multiple movement patterns
 * - Dynamic parameter updates
 * - Trail effect for spread mode
 */

interface UseBLSAnimationProps {
  elementRef: RefObject<HTMLElement>;
  isPlaying: boolean;
  direction: Direction;
  speed: number;
  color: string;
  size: number; // Circle diameter in pixels
  movementStyle: MovementStyle;
  onPanUpdate?: (panValue: number) => void;
  onTrailUpdate?: (positions: Position[]) => void;
  onOpacityUpdate?: (opacity: number) => void;
}

export function useBLSAnimation({
  elementRef,
  isPlaying,
  direction,
  speed,
  color,
  size,
  movementStyle,
  onPanUpdate,
  onTrailUpdate,
  onOpacityUpdate,
}: UseBLSAnimationProps): void {
  const engineRef = useRef(getAnimationEngine());

  // Effect for start/stop
  // NOTE: Only depends on isPlaying - parameter changes are handled by separate update effects below
  useEffect(() => {
    const engine = engineRef.current;
    const element = elementRef.current;

    if (!element) return;

    if (isPlaying) {
      engine.start(element, { direction, speed, color, size, movementStyle, onPanUpdate, onTrailUpdate, onOpacityUpdate });
    } else {
      engine.stop();
    }

    return () => {
      engine.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, isPlaying, size]);

  // Effect for updating color while playing
  useEffect(() => {
    const engine = engineRef.current;
    if (isPlaying) {
      engine.updateColor(color);
    }
  }, [color, isPlaying]);

  // Effect for updating direction while playing
  useEffect(() => {
    const engine = engineRef.current;
    if (isPlaying) {
      engine.updateDirection(direction);
    }
  }, [direction, isPlaying]);

  // Effect for updating speed while playing
  useEffect(() => {
    const engine = engineRef.current;
    if (isPlaying) {
      engine.updateSpeed(speed);
    }
  }, [speed, isPlaying]);

  // Effect for updating movement style while playing
  useEffect(() => {
    const engine = engineRef.current;
    if (isPlaying) {
      engine.updateMovementStyle(movementStyle);
    }
  }, [movementStyle, isPlaying]);

  // Effect for updating callbacks (ensures latest callbacks are used)
  useEffect(() => {
    const engine = engineRef.current;
    engine.updateCallbacks(onPanUpdate, onTrailUpdate, onOpacityUpdate);
  }, [onPanUpdate, onTrailUpdate, onOpacityUpdate]);
}
